import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import taskApi from '../services/taskApi';
import secureApiService from '../services/secureApi';
import userApi from '../services/userApi';
import useLanguage from '../hooks/useLanguage';
import SuppliesPicker from './SuppliesPicker';
import { MANUAL_TASK_TYPES } from '../config/taskTypes';
import { taskRefusalKey } from '../utils/taskErrorCodes';

const CreateTaskModal = ({ show, onHide, onTaskCreated, initialPropertyId = null }) => {
  const { t, isArabic } = useLanguage();

  const PRIORITY_OPTIONS = [
    { value: 'low', label: t('tasks.priority.label.low'), color: 'primary' },
    { value: 'medium', label: t('tasks.priority.label.medium'), color: 'success' },
    { value: 'high', label: t('tasks.priority.label.high'), color: 'info' },
    { value: 'urgent', label: t('tasks.priority.label.urgent'), color: 'warning' },
    { value: 'critical', label: t('tasks.priority.label.critical'), color: 'danger' }
  ];

  const [properties, setProperties] = useState([]);
  const [maintenanceUsers, setMaintenanceUsers] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  // Earliest selectable date = the server's business date. UX guidance only —
  // the create endpoint enforces the same bound, resolved the same way.
  const [minScheduledDate, setMinScheduledDate] = useState('');
  // The supplies the «مستلزمات ضيافة» type ticks from — the active cleaning template, from the
  // server. Empty (nothing seeded, or an older backend) means that type is simply not offered.
  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // The refill task a supplies request joined, shown as its own LTR run: a ticket sitting bare in an
  // Arabic sentence reads backwards (utils/numberFormatter.js documents the same bidi trap).
  const [successTicket, setSuccessTicket] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    type: 'cleaning',
    property: initialPropertyId || '',
    title: '',
    scheduledDate: '',
    scheduledTime: '',
    priority: 'medium',
    assignedTo: '',
    notes: '',
    restockItemIds: []
  });

  const isRestock = formData.type === 'restock';
  // A refill task is titled by the system, so ops never type one (tasks/manual-task-type.md D9).
  const canSubmit = !!formData.property && !!formData.scheduledDate &&
    (isRestock ? formData.restockItemIds.length > 0 : !!formData.title);

  // Load properties and reset form when modal opens
  useEffect(() => {
    const loadPropertiesAndReset = async () => {
      if (!show) return;

      // Load properties
      setLoadingProperties(true);
      try {
        const response = await secureApiService.getProperties();
        setProperties(response || []); // response is already the properties array
      } catch (err) {
        console.error('Error loading properties:', err);
        setError(t('tasks.create.propertyError'));
      } finally {
        setLoadingProperties(false);
      }

      // Load maintenance users
      try {
        const response = await userApi.getStaffUsers();
        setMaintenanceUsers(response.users || []);
      } catch (err) {
        console.error('Error loading maintenance users:', err);
      }

      // Reset form. The scheduled-date default comes from the SERVER (business
      // date, Riyadh-resolved). It used to be toISOString(), which returns the
      // UTC day and so pre-filled yesterday between 00:00 and 03:00 Riyadh —
      // scheduling a task on the wrong day for anyone working late.
      // Blank on failure rather than silently wrong.
      let defaultScheduledDate = '';
      let supplyOptions = [];
      try {
        const response = await taskApi.getFormDefaults();
        defaultScheduledDate = response?.data?.defaults?.scheduledDate || '';
        // Read defensively: a web build deployed ahead of the backend gets no options at all.
        supplyOptions = response?.data?.options?.supplies || [];
      } catch (err) {
        console.error('Error loading task form defaults:', err);
      }
      setMinScheduledDate(defaultScheduledDate);
      setSupplies(supplyOptions);

      setFormData({
        type: 'cleaning',
        property: initialPropertyId || '',
        title: '',
        scheduledDate: defaultScheduledDate,
        scheduledTime: '',
        priority: 'medium',
        assignedTo: '',
        notes: '',
        restockItemIds: []
      });
      setError(null);
      setSuccess(null);
      setSuccessTicket(null);
    };

    loadPropertiesAndReset();
  }, [show, initialPropertyId]); // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleType = (type) => {
    setError(null);
    setFormData(prev => ({ ...prev, type }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.property) {
        setError(t('tasks.create.propertyRequired'));
        setLoading(false);
        return;
      }
      if (!isRestock && !formData.title) {
        setError(t('tasks.create.taskTitleRequired'));
        setLoading(false);
        return;
      }
      if (isRestock && formData.restockItemIds.length === 0) {
        setError(t('tasks.create.suppliesRequired'));
        setLoading(false);
        return;
      }
      if (!formData.scheduledDate) {
        setError(t('tasks.create.dateRequired'));
        setLoading(false);
        return;
      }

      // Prepare task data
      const taskData = {
        propertyId: formData.property,
        type: formData.type,
        scheduledDate: formData.scheduledDate,
        priority: formData.priority
      };

      if (isRestock) {
        taskData.restockItemIds = formData.restockItemIds;
      } else {
        taskData.title = formData.title;
      }

      // Add optional fields if provided
      if (formData.scheduledTime) {
        taskData.scheduledTime = formData.scheduledTime;
      }
      if (formData.assignedTo) {
        taskData.assignedTo = formData.assignedTo;
      }
      if (formData.notes) {
        taskData.notes = formData.notes;
      }

      // Create task via API
      const response = await taskApi.createTask(taskData);

      // A supplies request joins the unit's open refill task when there is one, so say which task
      // it went to — otherwise ops look for a new one that was never created (D6).
      const ticketId = response?.data?.task?.ticketId;
      const joined = !!response?.data?.merged && !!ticketId;
      setSuccess(joined ? t('tasks.create.mergedSuccess') : t('tasks.create.success'));
      setSuccessTicket(joined ? ticketId : null);
      setError(null);

      // Close modal after showing success for 1.5 seconds
      setTimeout(() => {
        setSuccess(null);
        setSuccessTicket(null);
        if (onTaskCreated) {
          onTaskCreated();
        }
        onHide();
      }, 1500);
    } catch (err) {
      console.error('Error creating task:', err);
      // The server names the part it refused with a code (TASK_006 + details.field); its Arabic
      // sentence is only the fallback for a code this build does not know (I18N_STRATEGY).
      const key = taskRefusalKey(err.response?.data);
      setError(key ? t(key) : (err.response?.data?.error || t('tasks.create.error')));
    } finally {
      setLoading(false);
    }
  };

  const dir = isArabic ? 'rtl' : 'ltr';
  // The refill type only exists while there is a supplies list to tick from.
  const typeOptions = MANUAL_TASK_TYPES.filter(type => type !== 'restock' || supplies.length > 0);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dir={dir}>
      <Modal.Header closeButton>
        <Modal.Title className="w-100">{t('tasks.create.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success">
            {success}
            {successTicket && <> <bdi dir="ltr">{successTicket}</bdi></>}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* What kind of job this is — it decides what the rest of the form asks for */}
          <Form.Group className="mb-3">
            <Form.Label className="d-block">{t('tasks.create.typeLabel')}</Form.Label>
            <div className="d-flex flex-wrap gap-2" role="group" aria-label={t('tasks.create.typeLabel')}>
              {typeOptions.map(type => {
                const active = formData.type === type;
                return (
                  <button
                    key={type}
                    type="button"
                    className="touch-btn"
                    onClick={() => handleType(type)}
                    disabled={loading}
                    aria-pressed={active}
                    style={{
                      flex: '1 1 0',
                      minWidth: '120px',
                      border: '2px solid #471396',
                      borderRadius: '22px',
                      background: active ? '#471396' : 'transparent',
                      color: active ? '#fff' : '#471396',
                      fontWeight: 600
                    }}
                  >
                    {t(`tasks.type.${type}`)}
                  </button>
                );
              })}
            </div>
          </Form.Group>

          {/* Property Selection - Only show if not on property page */}
          {!initialPropertyId && (
            <Form.Group className="mb-3">
              <Form.Label className="d-block">{t('tasks.create.propertyLabel')}</Form.Label>
              <Form.Select
                name="property"
                value={formData.property}
                onChange={handleChange}
                required
                disabled={loadingProperties}
                dir={dir}
                style={{ textAlign: isArabic ? 'right' : 'left' }}
              >
                {loadingProperties ? (
                  <option value="">{t('tasks.create.propertyLoading')}</option>
                ) : (
                  <>
                    <option value="">{t('tasks.create.propertySelect')}</option>
                    {properties.map(property => (
                      <option key={property._id} value={property._id}>
                        {property.name}
                      </option>
                    ))}
                  </>
                )}
              </Form.Select>
            </Form.Group>
          )}

          {/* A refill: tick what the unit needs. No title — the system writes it, so every refill
              task reads alike whoever raised it. */}
          {isRestock ? (
            <Form.Group className="mb-3">
              <Form.Label className="d-block">{t('tasks.create.suppliesLabel')}</Form.Label>
              <SuppliesPicker
                items={supplies}
                selected={formData.restockItemIds}
                disabled={loading}
                label={t('tasks.create.suppliesLabel')}
                onChange={(restockItemIds) => setFormData(prev => ({ ...prev, restockItemIds }))}
              />
              <Form.Text className="text-muted d-block mt-2">
                {t('tasks.create.suppliesHint')}
              </Form.Text>
            </Form.Group>
          ) : (
            <Form.Group className="mb-3">
              <Form.Label className="d-block">{t('tasks.create.taskTitleLabel')}</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder={formData.type === 'maintenance'
                  ? t('tasks.create.maintenanceTitlePlaceholder')
                  : t('tasks.create.taskTitlePlaceholder')}
                required
                maxLength={200}
                dir={dir}
                style={{ textAlign: isArabic ? 'right' : 'left' }}
              />
            </Form.Group>
          )}

          {/* Date and Time */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="d-block">{t('tasks.create.dateLabel')}</Form.Label>
                <Form.Control
                  type="date"
                  name="scheduledDate"
                  value={formData.scheduledDate}
                  onChange={handleChange}
                  min={minScheduledDate || undefined}
                  required
                  dir={dir}
                  style={{ textAlign: isArabic ? 'right' : 'left' }}
                />
                <Form.Text className="text-muted d-block">
                  {t('tasks.create.dateHint')}
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="d-block">{t('tasks.create.timeLabel')}</Form.Label>
                <Form.Control
                  type="time"
                  name="scheduledTime"
                  value={formData.scheduledTime}
                  onChange={handleChange}
                  dir={dir}
                  style={{ textAlign: isArabic ? 'right' : 'left' }}
                />
              </Form.Group>
            </div>
          </div>

          {/* Priority */}
          <Form.Group className="mb-3">
            <Form.Label className="d-block">{t('tasks.create.priorityLabel')}</Form.Label>
            <Form.Select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              dir={dir}
              style={{ textAlign: isArabic ? 'right' : 'left' }}
            >
              {PRIORITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Assigned To */}
          <Form.Group className="mb-3">
            <Form.Label className="d-block">{t('tasks.create.assignLabel')}</Form.Label>
            <Form.Select
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleChange}
              dir={dir}
              style={{ textAlign: isArabic ? 'right' : 'left' }}
            >
              <option value="">{t('tasks.create.assignEmpty')}</option>
              {maintenanceUsers.map(user => (
                <option key={user._id} value={user._id}>
                  {user.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted d-block">
              {t('tasks.create.assignHint')}
            </Form.Text>
          </Form.Group>

          {/* Notes */}
          <Form.Group className="mb-3">
            <Form.Label className="d-block">{t('tasks.create.notesLabel')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder={t('tasks.create.notesPlaceholder')}
              maxLength={1000}
              dir={dir}
              style={{ textAlign: isArabic ? 'right' : 'left' }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer dir={dir} className={isArabic ? 'justify-content-start' : 'justify-content-end'}>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
        >
          {loading ? t('tasks.create.submitting') : t('tasks.create.submitBtn')}
        </Button>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {t('tasks.create.cancelBtn')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateTaskModal;
